import React, { ReactElement, useEffect, useState } from "react";
import { useCoastguard } from "@cofano/coastguard2-frontend-client";
import { useStoreActions } from "ezStore";
import { Navigate, useLocation } from "react-router-dom";
import { BOSSettings } from "types/commons";
import { useLazyQuery } from "@apollo/client";
import { convertSettingsToData } from "view/admin/utils/AdminUtils";
import { GetPublicSettingsDocument, LanguageSetByCodeDocument } from "__generated__/view/admin/graphql/admin.queries.generated";
import { GetVersionDocument } from "__generated__/graphql/queries.generated";

export default function LoginRoute({ children }: { children: ReactElement }) {

	const { instance: { isAuthenticated } } = useCoastguard();
	const { setInitialSettings } = useStoreActions(actions => actions.session);
	const location = useLocation();
	const state = location.state as { redirectLocation: string };

	const [ getLanguageSetByCode ] = useLazyQuery(LanguageSetByCodeDocument);
	const [ getApplicationVersion ] = useLazyQuery(GetVersionDocument);
	const [ getPublicSettings ] = useLazyQuery(GetPublicSettingsDocument);

	const [ latestVersion, setLatestVersion ] = useState<string | null>(null);

	useEffect(() => {
		if (!isAuthenticated) {
			Promise.all([
				getLanguageSetByCode({
					variables: { code: "EN" },
				}),
				getPublicSettings(),
				getApplicationVersion(),
			]).then(([ languages, publicSettings, applicationVersion ]) => {
				const languageEntries = languages.data?.languageSetByCode ?? [];

				const values = languageEntries.reduce<{ [key: string]: string }>((acc, cur) => {
					if (cur?.key) {
						acc[cur.key] = cur.translation ?? "";
					}
					return acc;
				}, {});

				const language = {
					language: "EN",
					values: values,
				};

				const settings = publicSettings.data ? convertSettingsToData(publicSettings.data?.publicSettings) : {};
				const version = applicationVersion.data?.version as string;

				setInitialSettings({
					language: language,
					publicSettings: settings as BOSSettings,
					version: version,
				});

				if (version) {
					setLatestVersion(version);
				}
			}).catch(() => {
				console.error("Something went wrong with retrieving initial/public settings");
			});
		}
	}, [ isAuthenticated ]);

	return !isAuthenticated ? React.cloneElement(children, {
		latestVersion: latestVersion,
	}) : (
		<Navigate to={state?.redirectLocation || "/view"} />
	);
}
